import { useState } from "react";
import { useQuery, useMutation } from "@tanstack/react-query";
import {
  Package,
  ChevronDown,
  ChevronRight,
  Check,
  Loader2,
  AlertCircle,
  ArrowRight,
} from "lucide-react";
import { releaseCyclesApi, environmentsApi } from "../api/client";
import { titleCase } from "../lib/utils";

interface RollupGenerationModalProps {
  releaseCycleId: string;
  onClose: () => void;
  onGenerated: (deploymentIds: string[]) => void;
}

export default function RollupGenerationModal({
  releaseCycleId,
  onClose,
  onGenerated,
}: RollupGenerationModalProps) {
  const [targetEnvironmentId, setTargetEnvironmentId] = useState("");
  const [selectedServices, setSelectedServices] = useState<Set<string>>(
    new Set(),
  );
  const [expandedServices, setExpandedServices] = useState<Set<string>>(
    new Set(),
  );

  const { data: environmentsData } = useQuery({
    queryKey: ["environments"],
    queryFn: () => environmentsApi.list(),
  });

  const { data: rollupServicesData, isLoading: servicesLoading } = useQuery({
    queryKey: ["rollup-services", releaseCycleId, targetEnvironmentId],
    queryFn: () =>
      releaseCyclesApi.getServicesForRollup(
        releaseCycleId,
        targetEnvironmentId,
      ),
    enabled: !!targetEnvironmentId,
  });

  const generateMutation = useMutation({
    mutationFn: () =>
      releaseCyclesApi.generateRollups(releaseCycleId, {
        service_ids: Array.from(selectedServices),
        target_environment_id: targetEnvironmentId,
      }),
    onSuccess: (data) => {
      onGenerated(data.deployments.map((d) => d.id));
    },
  });

  const environments = environmentsData?.environments || [];
  const prodEnvironments = environments.filter((e) => e.is_production);
  const services = rollupServicesData?.services || [];

  const toggleService = (serviceId: string) => {
    const newSelected = new Set(selectedServices);
    if (newSelected.has(serviceId)) {
      newSelected.delete(serviceId);
    } else {
      newSelected.add(serviceId);
    }
    setSelectedServices(newSelected);
  };

  const toggleExpanded = (serviceId: string) => {
    const newExpanded = new Set(expandedServices);
    if (newExpanded.has(serviceId)) {
      newExpanded.delete(serviceId);
    } else {
      newExpanded.add(serviceId);
    }
    setExpandedServices(newExpanded);
  };

  const selectAll = () => {
    setSelectedServices(new Set(services.map((s) => s.service_id)));
  };

  const clearAll = () => {
    setSelectedServices(new Set());
  };

  const handleGenerate = () => {
    if (selectedServices.size === 0) return;
    generateMutation.mutate();
  };

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
      <div className="bg-white rounded-lg shadow-xl w-full max-w-2xl mx-4 max-h-[80vh] flex flex-col">
        <div className="px-6 py-4 border-b border-gray-200">
          <h2 className="text-lg font-semibold text-gray-900">
            Generate Prod Rollups
          </h2>
          <p className="text-sm text-gray-500 mt-1">
            Create draft deployments for production with changes from completed
            UAT deployments
          </p>
        </div>

        <div className="px-6 py-4 flex-1 overflow-y-auto space-y-6">
          {/* Target Environment Selection */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Target Production Environment
            </label>
            <select
              value={targetEnvironmentId}
              onChange={(e) => {
                setTargetEnvironmentId(e.target.value);
                setSelectedServices(new Set());
              }}
              className="input w-full"
            >
              <option value="">Select environment...</option>
              {prodEnvironments.map((env) => (
                <option key={env.id} value={env.id}>
                  {env.name}
                </option>
              ))}
            </select>
          </div>

          {/* Services Selection */}
          {targetEnvironmentId && (
            <div>
              <div className="flex items-center justify-between mb-2">
                <label className="block text-sm font-medium text-gray-700">
                  Services with changes
                </label>
                {services.length > 0 && (
                  <div className="flex gap-2">
                    <button
                      onClick={selectAll}
                      className="text-xs text-brand-600 hover:text-brand-700"
                    >
                      Select All
                    </button>
                    <span className="text-gray-300">|</span>
                    <button
                      onClick={clearAll}
                      className="text-xs text-gray-500 hover:text-gray-700"
                    >
                      Clear
                    </button>
                  </div>
                )}
              </div>

              {servicesLoading ? (
                <div className="flex items-center justify-center py-8">
                  <Loader2 className="w-6 h-6 text-gray-400 animate-spin" />
                </div>
              ) : services.length === 0 ? (
                <div className="bg-gray-50 rounded-lg p-6 text-center">
                  <AlertCircle className="w-8 h-8 text-gray-400 mx-auto mb-2" />
                  <p className="text-sm text-gray-600">
                    No services with completed UAT deployments found for this
                    release cycle
                  </p>
                </div>
              ) : (
                <div className="border border-gray-200 rounded-lg divide-y divide-gray-200">
                  {services.map((service) => (
                    <div key={service.service_id}>
                      {/* Service Header */}
                      <div className="flex items-center gap-3 px-4 py-3 hover:bg-gray-50">
                        <input
                          type="checkbox"
                          checked={selectedServices.has(service.service_id)}
                          onChange={() => toggleService(service.service_id)}
                          className="h-4 w-4 rounded border-gray-300 text-brand-600 focus:ring-brand-500"
                        />
                        <button
                          onClick={() => toggleExpanded(service.service_id)}
                          className="flex items-center gap-2 flex-1 text-left"
                        >
                          {expandedServices.has(service.service_id) ? (
                            <ChevronDown className="w-4 h-4 text-gray-400" />
                          ) : (
                            <ChevronRight className="w-4 h-4 text-gray-400" />
                          )}
                          <span className="font-medium text-gray-900">
                            {titleCase(service.service_name)}
                          </span>
                        </button>
                        <span className="text-sm text-gray-500">
                          {service.component_count} component
                          {service.component_count !== 1 ? "s" : ""} changed
                        </span>
                      </div>

                      {/* Expanded Component List */}
                      {expandedServices.has(service.service_id) && (
                        <div className="bg-gray-50 px-4 py-3 pl-12">
                          <table className="w-full text-sm">
                            <thead>
                              <tr className="text-gray-500 text-xs uppercase">
                                <th className="text-left pb-2">Component</th>
                                <th className="text-left pb-2">Current</th>
                                <th className="text-center pb-2"></th>
                                <th className="text-left pb-2">New</th>
                              </tr>
                            </thead>
                            <tbody className="divide-y divide-gray-200">
                              {service.components.map((comp) => (
                                <tr key={comp.component_id}>
                                  <td className="py-2 flex items-center gap-2">
                                    <Package className="w-4 h-4 text-gray-400" />
                                    {comp.component_name}
                                  </td>
                                  <td className="py-2">
                                    <span className="font-mono text-xs bg-gray-200 text-gray-700 px-1.5 py-0.5 rounded">
                                      {comp.current_version || "N/A"}
                                    </span>
                                  </td>
                                  <td className="py-2 text-center">
                                    <ArrowRight className="w-4 h-4 text-gray-400 inline" />
                                  </td>
                                  <td className="py-2">
                                    <span className="font-mono text-xs bg-green-100 text-green-700 px-1.5 py-0.5 rounded">
                                      {comp.new_version}
                                    </span>
                                  </td>
                                </tr>
                              ))}
                            </tbody>
                          </table>
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}

          {/* Error Message */}
          {generateMutation.isError && (
            <div className="p-3 bg-red-50 border border-red-200 rounded-md text-sm text-red-600">
              {generateMutation.error instanceof Error
                ? generateMutation.error.message
                : "Failed to generate rollups"}
            </div>
          )}
        </div>

        <div className="px-6 py-4 border-t border-gray-200 flex justify-between items-center">
          <div className="text-sm text-gray-500">
            {selectedServices.size > 0 && (
              <span>
                {selectedServices.size} service
                {selectedServices.size !== 1 ? "s" : ""} selected
              </span>
            )}
          </div>
          <div className="flex gap-3">
            <button
              type="button"
              onClick={onClose}
              className="btn btn-secondary"
              disabled={generateMutation.isPending}
            >
              Cancel
            </button>
            <button
              onClick={handleGenerate}
              disabled={
                selectedServices.size === 0 ||
                !targetEnvironmentId ||
                generateMutation.isPending
              }
              className="btn btn-primary"
            >
              {generateMutation.isPending ? (
                <>
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  Generating...
                </>
              ) : (
                <>
                  <Check className="w-4 h-4 mr-2" />
                  Generate Rollups
                </>
              )}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
